import React from 'react';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const dayNames = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function getMonthMatrix(year: number, month: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const matrix = [];
  let week = [];
  
  // Calculate start offset for Monday-based week
  let dayOfWeek = firstDay.getDay();
  let startOffset = (dayOfWeek + 6) % 7;
  
  // Add previous month's dates
  if (startOffset > 0) {
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const prevMonthLastDay = new Date(prevYear, prevMonth + 1, 0).getDate();
    
    for (let i = startOffset - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      week.push({ day, isCurrentMonth: false, year: prevYear, month: prevMonth });
    }
  }
  
  // Add current month's dates
  for (let d = 1; d <= lastDay.getDate(); d++) {
    week.push({ day: d, isCurrentMonth: true, year, month });
    if (week.length === 7) {
      matrix.push(week);
      week = [];
    }
  }
  
  // Add next month's dates to complete the last week
  if (week.length > 0) {
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    let nextMonthDay = 1;
    
    while (week.length < 7) {
      week.push({ day: nextMonthDay, isCurrentMonth: false, year: nextYear, month: nextMonth });
      nextMonthDay++;
    }
    matrix.push(week);
  }
  
  return matrix;
}

interface CalendarProps {
  year: number;
  month: number; // 0-based
  availableDates: string[]; // yyyy-mm-dd
  selectedDate: string;
  onSelectDate: (date: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

/**
 * Calendar - A calendar component for date selection.
 *
 * Displays a monthly calendar view with navigation controls and date selection capabilities.
 * The component shows the current month with previous/next month navigation and highlights
 * available dates for selection.
 *
 * @param year - The year to display
 * @param month - The month to display (0-11, where 0 is January)
 * @param availableDates - Array of available dates in YYYY-MM-DD format
 * @param selectedDate - Currently selected date in YYYY-MM-DD format
 * @param onSelectDate - Callback function when a date is selected
 * @param onPrevMonth - Callback function for previous month navigation
 * @param onNextMonth - Callback function for next month navigation
 *
 * @example
 * ```tsx
 * <Calendar
 *   year={2025}
 *   month={0}
 *   availableDates={['2025-01-15', '2025-01-16']}
 *   selectedDate="2025-01-15"
 *   onSelectDate={(date) => setSelectedDate(date)}
 *   onPrevMonth={() => setMonth(month - 1)}
 *   onNextMonth={() => setMonth(month + 1)}
 * />
 * ```
 */
const Calendar: React.FC<CalendarProps> = ({
  year,
  month,
  availableDates,
  selectedDate,
  onSelectDate,
  onPrevMonth,
  onNextMonth,
}) => {
  const matrix = getMonthMatrix(year, month);
  const monthLabel = `${monthNames[month]} ${year}`;
  const formatDate = (y: number, m: number, d: number) =>
    `${y}-${(m + 1).toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;

  return (
    <div style={{ maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }} role="application" aria-label={`Calendar for ${monthLabel}`}>
      <div style={{ padding: '18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, backgroundColor: '#eef7ff', borderRadius: 8 }}>
          <button
            style={{ background: 'none', border: 'none', fontSize: 20, color: '#1a355e', cursor: 'pointer', fontWeight: 'bold', padding: '0 0 5px 10px', margin: '0', minWidth: 'auto' }}
            onClick={onPrevMonth}
            aria-label={`Previous month, ${monthNames[month === 0 ? 11 : month - 1]} ${month === 0 ? year - 1 : year}`}
          >←</button>
          <span style={{ fontWeight: 700, color: '#185F93', fontSize: '0.8rem' }}>{monthLabel}</span>
          <button
            style={{ background: 'none', border: 'none', fontSize: 20, color: '#1a355e', cursor: 'pointer', fontWeight: 'bold', padding: '0 10px 5px 0', margin: '0', minWidth: 'auto' }}
            onClick={onNextMonth}
            aria-label={`Next month, ${monthNames[month === 11 ? 0 : month + 1]} ${month === 11 ? year + 1 : year}`}
          >→</button>
        </div>
      </div>
      <table style={{ width: '100%', textAlign: 'center', borderCollapse: 'collapse', fontSize: '1rem', color: '#185F93', marginBottom: 0, tableLayout: 'fixed' }} role="grid" aria-label={`${monthLabel} calendar`}>
        <thead>
          <tr style={{ color: '#b5c7d6', fontWeight: 600, borderBottom: '3px solid #ddd' }}>
            {dayNames.map((d, index) => {
              const isFirst = index === 0;
              const isLast = index === dayNames.length - 1;
              const thStyle = {
                ...(isFirst && { borderLeft: '10px solid transparent' }),
                ...(isLast && { borderRight: '10px solid transparent' })
              };
              return (
                <th 
                  key={`day-${index}`} 
                  scope="col" 
                  style={thStyle}
                  aria-label={['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][index]}
                >
                  {d}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {matrix.map((week, i) => (
            <tr key={i}>
              {week.map((d, j) => {
                if (!d) return <td key={j} style={{ padding: 0 }}></td>;
                
                const dateStr = formatDate(d.year, d.month, d.day);
                const isAvailable = d.isCurrentMonth && availableDates.includes(dateStr);
                const isSelected = selectedDate === dateStr;
                const dateLabel = `${monthNames[d.month]} ${d.day}, ${d.year}`;
                
                // Style for previous/next month dates
                const isAdjacentMonth = !d.isCurrentMonth;
                const buttonStyle = {
                  width: '100%',
                  minWidth: 32,
                  maxWidth: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: 'none',
                  background: isSelected ? '#e69e6d' : isAvailable ? '#98B7D3' : 'transparent',
                  color: isSelected ? '#fff' : isAvailable ? '#fff' : isAdjacentMonth ? '#b5c7d6' : '#185F93',
                  fontWeight: isSelected ? 700 : isAdjacentMonth ? 400 : 600,
                  cursor: isAvailable ? 'pointer' : 'default',
                  outline: 'none',
                  margin: '2px',
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isAdjacentMonth ? 0.6 : 1,
                };
                
                return (
                  <td key={j} style={{ padding: 0 }}>
                    <button
                      style={buttonStyle}
                      disabled={!isAvailable}
                      onClick={() => isAvailable && onSelectDate(dateStr)}
                      aria-label={`${dateLabel}${isSelected ? ', selected' : ''}${!isAvailable ? ', not available' : ''}${isAdjacentMonth ? ', adjacent month' : ''}`}
                      aria-disabled={!isAvailable}
                    >
                      {d.day}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Calendar; 